import moment, { Moment } from 'moment'
import { sortedIndex } from 'lodash'

import { FocusedInput, DaysAvailabilities } from './types'

type FindAvailability = (arg0: {
  day: Moment
  daysData: DaysAvailabilities
}) => DaysAvailabilities[string]

export const findAvailability: FindAvailability = ({ day, daysData }) => {
  return daysData[day.format('YYYY-MM-DD')]
}

function isPreviousDayAvailable(
  day: Moment,
  daysData: DaysAvailabilities
): boolean {
  const prevDay = findAvailability({
    day: moment(day).subtract(1, 'day'),
    daysData,
  })

  if (prevDay) {
    return prevDay.available
  }
  return true
}

export type FindNextUnavailableDay = (date: string) => Moment | null

type BuildFindNextUnavailableDayOptions = {
  daysData: DaysAvailabilities
}

export function buildFindNextUnavailableDay({
  daysData,
}: BuildFindNextUnavailableDayOptions): FindNextUnavailableDay {
  // `daysData` keys are dates in the ISO date format `'YYYY-MM-DD`, we can take
  // advantage of the lexicographical ordering of ISO dates to perform a binary
  // search over a sorted set of unavailable dates:
  const sortedUnavailableDates = Object.keys(daysData)
    .filter((day) => !daysData[day].available)
    .sort()

  return (date) => {
    const nextUnavailableDateIndex = sortedIndex(sortedUnavailableDates, date)

    if (nextUnavailableDateIndex === sortedUnavailableDates.length) {
      // No unavailable date was found, return null:
      return null
    }

    return moment(sortedUnavailableDates[nextUnavailableDateIndex]).endOf('day')
  }
}

type DayStatusFunction = (day: Moment, dayDate?: string) => boolean
function cachedDayResult(dayFn: DayStatusFunction): DayStatusFunction {
  const cache = new Map<string, boolean>()

  return (day) => {
    const dayDate = day.format('YYYY-MM-DD')
    if (cache.has(dayDate)) {
      return cache.get(dayDate)
    }

    const result = dayFn(day, dayDate)
    cache.set(dayDate, result)
    return result
  }
}

type IsDayBlocked = DayStatusFunction

type BuildIsDayBlockedOptions = {
  focusedInput: FocusedInput
  startDate?: Moment
  daysData: DaysAvailabilities
}

export function buildIsDayBlocked({
  focusedInput,
  startDate,
  daysData,
}: BuildIsDayBlockedOptions): IsDayBlocked {
  return cachedDayResult((day, dayDate) => {
    const availability = daysData[dayDate]
    const isAvailable = availability?.available !== false

    // Only perform check-out only test if there was an availability record
    // indicating that it was unavailable:
    if (availability && !isAvailable && focusedInput === 'endDate') {
      if (!startDate || day.isAfter(startDate)) {
        // Dates that are unavailable but where the previous day is available are
        // not blocked for the purposes of selecting an end date because they can
        // be used for check-out only:
        const previousDayAvailable = isPreviousDayAvailable(day, daysData)
        if (previousDayAvailable) {
          return false
        }
      }
    }

    return !isAvailable
  })
}

function supportsMinimumNights(
  day: Moment,
  dayDate: string,
  daysData: DaysAvailabilities,
  findNextUnavailableDay: FindNextUnavailableDay
) {
  const availability = daysData[dayDate]
  const isAvailable = availability?.available !== false
  const minimumNights = availability?.minimumNights

  if (isAvailable && minimumNights > 0) {
    const unavailableDay = findNextUnavailableDay(dayDate)
    if (unavailableDay && unavailableDay.diff(day, 'days') < minimumNights) {
      // There aren't enough available days after the provided day to meet the
      // minimum nights requirements:
      return false
    }
  }

  return true
}

type IsOutsideRange = DayStatusFunction

type BuildIsOutsideRangeOptions = {
  today: Moment
  focusedInput: FocusedInput
  startDate?: Moment
  maxStart?: Moment
  daysData: DaysAvailabilities
  firstUnavailableDate?: Moment
  findNextUnavailableDay: FindNextUnavailableDay
}

export function buildIsOutsideRange({
  today,
  focusedInput,
  startDate,
  maxStart,
  daysData,
  firstUnavailableDate,
  findNextUnavailableDay,
}: BuildIsOutsideRangeOptions): IsOutsideRange {
  if (focusedInput === 'startDate') {
    return cachedDayResult((day, dayDate) => {
      // Dates in the past cannot be selected:
      if (day.isBefore(today)) {
        return true
      }

      if (maxStart && day.isAfter(maxStart)) {
        // Dates beyond the maximum start date cannot be selected for a
        // start date:
        return true
      }

      return !supportsMinimumNights(
        day,
        dayDate,
        daysData,
        findNextUnavailableDay
      )
    })
  }

  return cachedDayResult((day, dayDate) => {
    // Dates in the past cannot be selected:
    if (day.isBefore(today)) {
      return true
    }

    if (startDate && day.isBefore(startDate)) {
      // When selecting an end date it is also possible to choose a new start
      // date, so we only want to allows days that support minimum nights:
      return !supportsMinimumNights(
        day,
        dayDate,
        daysData,
        findNextUnavailableDay
      )
    }

    if (firstUnavailableDate) {
      // If day is beyond the first unavailable day after `startDate`, then
      // it cannot be selected for an end date (the first unavailable day
      // itself can be selected because it is valid as a checkout day):
      return day.isAfter(firstUnavailableDate)
    }

    return false
  })
}
